import { Button } from './ui/button';
import type { Product as ProductType } from '@/types/index';
import { useCartStore } from '@/store/cart';
import { cn } from '@/lib/utils';
import { Minus, Plus } from 'lucide-react';
import { useState } from 'react';

function Tag({ text, type }: { text: string; type: 'green' | 'red' }) {
  return (
    <div
      className={`absolute top-0 right-0 bg-heineken-green text-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
        type === 'green' ? 'bg-heineken-green' : 'bg-heineken-red'
      }`}
    >
      {text}
    </div>
  );
}

interface ProductProps {
  product: ProductType;
}

export default function Product({ product }: ProductProps) {
  const { addProduct, products, getTotalCartCredits, availableCredits } =
    useCartStore();
  const [quantity, setQuantity] = useState(1);
  const cartItem = products.find((p) => p.id === product.id);
  const totalCartCredits = getTotalCartCredits();
  const hasEnoughCredits =
    availableCredits >= totalCartCredits + product.price * quantity;

  const handleAddToCart = () => {
    addProduct(product, quantity);
    setQuantity(1);
  };

  const handleIncrement = () => {
    if (hasEnoughCredits) {
      setQuantity((prev) => prev + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const buttonDisabled = !hasEnoughCredits;
  const buttonText = !hasEnoughCredits ? 'Nincs elég kredit' : 'Hozzáad';

  return (
    <div className="group flex flex-col bg-[#F7F7F7]">
      <div className="bg-grey group-hover:bg-green transition-all duration-300 bg-cover bg-center aspect-square flex flex-col items-center justify-center relative">
        {product.tags && <Tag text={product.tags} type="green" />}
        <img
          src={product.image}
          alt={product.name}
          className="max-w-[60%] max-h-[60%] object-contain"
        />
        <p className="hidden xl:block font-heineken font-bold text-2xl group-hover:text-white mt-4">
          {product.price} kredit
        </p>

        <div className="hidden xl:flex absolute bottom-6 bg-white left-4 right-4 opacity-100 xl:opacity-0 translate-y-0 xl:translate-y-2 transition-all duration-300 xl:group-hover:opacity-100 xl:group-hover:translate-y-0 flex-col 2xl:flex-row gap-2 rounded-sm shadow-xl overflow-hidden">
          <div className="flex items-center  rounded-md pr-2 2xl:pr-0 pl-2">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleDecrement}
              disabled={quantity <= 1}
              className="size-8 hover:bg-slate-200"
              aria-label="Decrease quantity"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="flex-1 2xl:w-8 text-center text-black">
              {quantity}
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleIncrement}
              disabled={!hasEnoughCredits}
              className="size-8 hover:bg-slate-200"
              aria-label="Increase quantity"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={buttonDisabled}
            className={cn(
              'flex-1 rounded-t-none 2xl:rounded-l-none',
              buttonDisabled &&
                'bg-heineken-red text-white cursor-not-allowed hover:bg-heineken-red/90 '
            )}
            aria-label={`Add ${product.name} to cart`}
          >
            {buttonText}
          </Button>
        </div>

        {cartItem && (
          <div className="absolute top-0 left-0 bg-white text-heineken-red px-2 py-1 text-[10px] font-bold">
            {cartItem.quantity}x kosárban
          </div>
        )}
      </div>
      <div className="p-4 relative">
        <div className="flex xl:hidden bg-white mb-3 opacity-100 xl:opacity-0 translate-y-0 flex-col gap-2 rounded-sm shadow-sm overflow-hidden">
          <div className="flex items-center  rounded-md pr-2 2xl:pr-0 pl-2">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleDecrement}
              disabled={quantity <= 1}
              className="size-8 hover:bg-slate-200"
              aria-label="Decrease quantity"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="flex-1 2xl:w-8 text-center text-black">
              {quantity}
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleIncrement}
              disabled={!hasEnoughCredits}
              className="size-8 hover:bg-slate-200"
              aria-label="Increase quantity"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={buttonDisabled}
            className={cn(
              'bg-heineken-green hover:bg-heineken-dark-green text-white flex-1 rounded-t-none 2xl:rounded-l-none',
              buttonDisabled &&
                'bg-heineken-red text-white cursor-not-allowed hover:bg-heineken-red/90 '
            )}
            aria-label={`Add ${product.name} to cart`}
          >
            {buttonText}
          </Button>
        </div>

        <p className="xl:hidden font-heineken font-bold text-2xl">
          {product.price} kredit
        </p>
        <p className="text-lg mb-1 font-bold">{product.name}</p>
        <p className="text-base">{product.description}</p>
      </div>
    </div>
  );
}
